import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, Calculator, Search, Wrench, FileText, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const STEPS = [
  {
    title: 'Deal Analyzer',
    description: 'Analyze any property in seconds. Enter an address, set your numbers, and get instant ROI across 6 strategies.',
    icon: Calculator,
    color: 'purple',
    action: '/analyzer',
    cta: 'Try It',
  },
  {
    title: 'Comp Finder',
    description: 'Find comparable sales and rental data to determine the true ARV of any property.',
    icon: Search,
    color: 'pink',
    action: '/comp-finder',
    cta: 'Find Comps',
  },
  {
    title: 'Repair Estimator',
    description: 'Get room-by-room rehab cost breakdowns. Choose budget, mid-range, or premium tiers.',
    icon: Wrench,
    color: 'orange',
    action: '/repair-estimator',
    cta: 'Estimate Costs',
  },
  {
    title: 'Export & Share',
    description: 'Export professional PDF reports and send contracts for e-signature with DocuSign.',
    icon: FileText,
    color: 'green',
    action: '/analyzer',
    cta: 'Get Started',
  },
];

const colorMap = {
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400', ring: 'ring-purple-400' },
  pink: { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400', ring: 'ring-pink-400' },
  orange: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400', ring: 'ring-orange-400' },
  green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400', ring: 'ring-green-400' },
};

const OnboardingGuide = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const dismissed = localStorage.getItem('onboarding_dismissed');
    const loginCount = parseInt(localStorage.getItem('login_count') || '0', 10);
    // Show on first 3 logins
    if (!dismissed && loginCount <= 3) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem('onboarding_dismissed', 'true');
  };

  const goToStep = (idx) => setCurrentStep(idx);

  const handleAction = () => {
    navigate(STEPS[currentStep].action);
    dismiss();
  };

  if (!visible) return null;

  const step = STEPS[currentStep];
  const colors = colorMap[step.color];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="onboarding-guide">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 p-6 relative">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="onboarding-dismiss"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-yellow-200" />
            <h2 className="text-white text-xl font-bold">Welcome to PropertyARV</h2>
          </div>
          <p className="text-white/80 text-sm mt-1">Let us show you around</p>
        </div>

        {/* Step Content */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className={`${colors.bg} rounded-xl p-3 shrink-0`}>
              <Icon className={`w-6 h-6 ${colors.text}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{step.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{step.description}</p>
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex gap-2 justify-center mb-6">
            {STEPS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToStep(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === currentStep
                    ? 'bg-purple-600 w-6'
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                }`}
                data-testid={`onboarding-step-${idx}`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={dismiss}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              data-testid="onboarding-skip"
            >
              Skip tour
            </button>
            <div className="flex gap-2">
              {currentStep < STEPS.length - 1 ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAction}
                    data-testid="onboarding-try-btn"
                  >
                    {step.cta}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => goToStep(currentStep + 1)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="onboarding-next-btn"
                  >
                    Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={handleAction}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white"
                  data-testid="onboarding-finish-btn"
                >
                  {step.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuide;
